import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Collapse, Chip, Box } from "@material-ui/core";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { client } from "services/axiosClient";
import useThemeColors from "hooks/useThemeColors";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardIcon from "components/Card/CardIcon";
import CardBody from "components/Card/CardBody";
import FeedbackButton from "components/CTSGuide/FeedbackButton";
import LocalShippingOutlined from "@material-ui/icons/LocalShippingOutlined";
import AttachMoneyOutlined from "@material-ui/icons/AttachMoneyOutlined";
import StorefrontOutlined from "@material-ui/icons/StorefrontOutlined";
import TrendingUpOutlined from "@material-ui/icons/TrendingUpOutlined";
import MoreHorizOutlined from "@material-ui/icons/MoreHorizOutlined";
import LoyaltyOutlined from "@material-ui/icons/LoyaltyOutlined";
import CardGiftcardOutlined from "@material-ui/icons/CardGiftcardOutlined";
import AccountBalanceOutlined from "@material-ui/icons/AccountBalanceOutlined";
import ExpandMore from "@material-ui/icons/ExpandMore";
import ExpandLess from "@material-ui/icons/ExpandLess";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import { grayColor, whiteColor } from "assets/jss/material-dashboard-pro-react";
import ctsGuidePortalStyle from "assets/jss/material-dashboard-pro-react/views/ctsGuidePortalStyle";
import CTSMiniNav from "components/CTSGuide/CTSMiniNav";

const useStyles = makeStyles((theme) => ({
  ...ctsGuidePortalStyle(theme),
  pageHeader: {
    marginBottom: "16px",
  },
  pageSubtitle: {
    fontSize: "0.9rem",
    color: grayColor[3],
    margin: 0,
  },
  mainLayout: {
    display: "grid",
    gridTemplateColumns: "200px 1fr",
    gap: "32px",
    minHeight: "400px",
  },
  sidebar: {
    position: "sticky",
    top: "20px",
    alignSelf: "start",
    backgroundColor: whiteColor,
    borderRadius: "8px",
    padding: "14px 10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  sidebarTitle: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: grayColor[4],
    marginBottom: "12px",
    paddingLeft: "10px",
  },
  categoryItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 12px",
    marginBottom: "2px",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontSize: "0.9rem",
    color: grayColor[2],
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  categoryItemActive: {
    backgroundColor: (props) => props.clientColor || grayColor[2],
    color: (props) => props.clientTextColor || whiteColor,
    fontWeight: 500,
    "&:hover": {
      backgroundColor: (props) => props.clientColor || grayColor[2],
    },
  },
  categoryIcon: {
    fontSize: "1.1rem",
    opacity: 0.85,
  },
  categoryCount: {
    marginLeft: "auto",
    fontSize: "0.7rem",
    fontWeight: 600,
    opacity: 0.7,
  },
  contentArea: {
    paddingBottom: "20px",
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "20px",
    padding: "14px 16px",
    backgroundColor: (props) => `${props.clientColor || grayColor[2]}08`,
  },
  categoryHeaderIcon: {
    fontSize: "1.5rem",
    color: (props) => props.clientColor || grayColor[2],
  },
  categoryHeaderTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: grayColor[1],
  },
  categoryHeaderCount: {
    fontSize: "0.8rem",
    color: grayColor[4],
    marginLeft: "auto",
  },
  lineGroup: {
    marginBottom: "12px",
    border: `1px solid ${grayColor[9]}`,
    borderRadius: "8px",
    backgroundColor: whiteColor,
    overflow: "hidden",
  },
  lineGroupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    cursor: "pointer",
    backgroundColor: grayColor[11],
    borderBottom: `1px solid ${grayColor[9]}`,
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: grayColor[10],
    },
  },
  lineGroupHeaderOpen: {
    backgroundColor: grayColor[10],
    borderLeft: (props) => `3px solid ${props.clientColor || grayColor[2]}`,
  },
  lineGroupTitle: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#1a1a1a",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  lineGroupBadge: {
    backgroundColor: (props) => `${props.clientColor || grayColor[2]}15`,
    color: (props) => props.clientColor || grayColor[2],
    fontSize: "0.7rem",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: "10px",
  },
  lineGroupExpandIcon: {
    fontSize: "1.3rem",
    color: grayColor[1],
  },
  lineGroupContent: {
    padding: "0",
  },
  criteriaCard: {
    padding: "16px 20px",
    borderBottom: `1px solid ${grayColor[10]}`,
    "&:last-child": {
      borderBottom: "none",
    },
  },
  criteriaName: {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: grayColor[1],
    marginBottom: "12px",
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  criteriaNameDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: (props) => props.clientColor || grayColor[2],
    flexShrink: 0,
    marginTop: "7px",
  },
  criteriaSection: {
    marginBottom: "12px",
    paddingLeft: "16px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  criteriaSectionLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: grayColor[2],
    marginBottom: "4px",
  },
  criteriaSectionText: {
    fontSize: "0.85rem",
    color: grayColor[2],
    lineHeight: 1.5,
  },
  // Specific styling for pros section
  prosSection: {
    marginBottom: "12px",
    paddingLeft: "16px",
    borderLeft: "2px solid #66bb6a",
    "& $criteriaSectionLabel": {
      color: "#43a047",
    },
    "& $criteriaSectionText": {
      color: grayColor[2],
    },
  },
  // Specific styling for cons section
  consSection: {
    marginBottom: "12px",
    paddingLeft: "16px",
    borderLeft: "2px solid #ffa726",
    "& $criteriaSectionLabel": {
      color: "#fb8c00",
    },
    "& $criteriaSectionText": {
      color: grayColor[2],
    },
  },
  formulaBlock: {
    background: "linear-gradient(135deg, #1a1a2e, #16213e)",
    color: "#a8dadc",
    padding: "16px 20px",
    borderRadius: "8px",
    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
    fontSize: "0.85rem",
    lineHeight: 1.7,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    marginTop: "6px",
    border: "1px solid #2d3a4f",
  },
  industriesContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    marginTop: "6px",
  },
  industryChip: {
    fontSize: "0.7rem",
    height: "22px",
    backgroundColor: grayColor[10],
    color: grayColor[2],
    fontWeight: 500,
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: grayColor[4],
  },
  emptyStateIcon: {
    fontSize: "3rem",
    opacity: 0.2,
    marginBottom: "16px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
}));

const CATEGORY_ICONS = {
  revenue_tax: TrendingUpOutlined,
  discounts: LoyaltyOutlined,
  costs: AttachMoneyOutlined,
  logistics: LocalShippingOutlined,
  commercial: StorefrontOutlined,
  trade_merch: CardGiftcardOutlined,
  other_expenses: AccountBalanceOutlined,
};

export default function CriteriaLibrary() {
  const { t, i18n } = useTranslation();
  const { clientColor, clientTextColor } = useThemeColors();
  const [selectedCategory, setSelectedCategory] = useState("revenue_tax");
  const [allCriteria, setAllCriteria] = useState([]); // All criteria loaded once
  const [allCriteriaCounts, setAllCriteriaCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  const classes = useStyles({ clientColor, clientTextColor });

  const lang = i18n.language?.substring(0, 2) || "pt";

  const categories = [
    {
      key: "revenue_tax",
      label: t("ctsguide.criteria.categories.revenue_tax"),
    },
    { key: "discounts", label: t("ctsguide.criteria.categories.discounts") },
    { key: "costs", label: t("ctsguide.criteria.categories.costs") },
    { key: "logistics", label: t("ctsguide.criteria.categories.logistics") },
    { key: "commercial", label: t("ctsguide.criteria.categories.commercial") },
    {
      key: "trade_merch",
      label: t("ctsguide.criteria.categories.trade_merch"),
    },
    {
      key: "other_expenses",
      label: t("ctsguide.criteria.categories.other_expenses"),
    },
  ];

  // Load all criteria once on mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Filter criteria locally when category changes (no API call needed)
  const criteria = useMemo(() => {
    return allCriteria.filter((item) => item.category === selectedCategory);
  }, [allCriteria, selectedCategory]);

  // Update expanded groups when filtered criteria changes
  useEffect(() => {
    const groups = {};
    criteria.forEach((item) => {
      const lineName =
        item.generic_line_name?.[lang] ||
        item.generic_line_name?.pt ||
        "Outros";
      groups[lineName] = true;
    });
    setExpandedGroups(groups);
  }, [criteria, lang]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const res = await client.get("/api/ctsguide/criteria-library-data/");
      setAllCriteria(res.data.criteria || []);
      setAllCriteriaCounts(res.data.counts || {});
    } catch (error) {
      setAllCriteria([]);
      setAllCriteriaCounts({});
    } finally {
      setLoading(false);
    }
  };

  const groupedCriteria = useMemo(() => {
    const groups = {};
    criteria.forEach((item) => {
      const lineName =
        item.generic_line_name?.[lang] ||
        item.generic_line_name?.pt ||
        "Outros";
      if (!groups[lineName]) {
        groups[lineName] = [];
      }
      groups[lineName].push(item);
    });
    return groups;
  }, [criteria, lang]);

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const getIndustryLabel = (industryKey) => {
    const industryLabels = {
      consumer_goods: t("ctsguide.industries.consumer_goods"),
      pharmaceutical: t("ctsguide.industries.pharmaceutical"),
      agribusiness: t("ctsguide.industries.agribusiness"),
      industrial: t("ctsguide.industries.industrial"),
      retail: t("ctsguide.industries.retail"),
      other: t("ctsguide.industries.other"),
    };
    return industryLabels[industryKey] || industryKey;
  };

  const CategoryIcon = CATEGORY_ICONS[selectedCategory] || MoreHorizOutlined;

  return (
    <div className={classes.pageContainer}>
      <Box display="flex" justifyContent="center" mb={0.5}>
        <CTSMiniNav />
      </Box>
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <LibraryBooksIcon />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>
                {t("ctsguide.nav.criteria_library")}
              </h4>
            </CardHeader>
            <CardBody>
              <div className={classes.pageHeader}>
                <p className={classes.pageSubtitle}>
                  {t("ctsguide.criteria.description")}
                </p>
              </div>

              <div className={classes.mainLayout}>
                <div className={classes.sidebar}>
                  <div className={classes.sidebarTitle}>
                    {t("ctsguide.criteria.categories_title")}
                  </div>
                  {categories.map((cat) => {
                    const CatIcon =
                      CATEGORY_ICONS[cat.key] || MoreHorizOutlined;
                    const isActive = selectedCategory === cat.key;
                    return (
                      <div
                        key={cat.key}
                        className={`${classes.categoryItem} ${
                          isActive ? classes.categoryItemActive : ""
                        }`}
                        onClick={() => setSelectedCategory(cat.key)}
                      >
                        <CatIcon className={classes.categoryIcon} />
                        <span>{cat.label}</span>
                        {allCriteriaCounts[cat.key] > 0 && (
                          <span className={classes.categoryCount}>
                            {allCriteriaCounts[cat.key]}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className={classes.contentArea}>
                  <div className={classes.categoryHeader}>
                    <CategoryIcon className={classes.categoryHeaderIcon} />
                    <span className={classes.categoryHeaderTitle}>
                      {
                        categories.find((c) => c.key === selectedCategory)
                          ?.label
                      }
                    </span>
                    <span className={classes.categoryHeaderCount}>
                      {criteria.length}{" "}
                      {criteria.length === 1
                        ? t("ctsguide.criteria.criteria_count_one")
                        : t("ctsguide.criteria.criteria_count_other")}
                    </span>
                  </div>

                  {loading && (
                    <div className={classes.loadingContainer}>
                      <ThreeDots
                        width="120px"
                        color={clientColor}
                        ariaLabel="loading"
                      />
                    </div>
                  )}

                  {!loading && Object.keys(groupedCriteria).length === 0 && (
                    <div className={classes.emptyState}>
                      <CategoryIcon className={classes.emptyStateIcon} />
                      <p>{t("ctsguide.criteria.no_criteria")}</p>
                    </div>
                  )}

                  {!loading &&
                    Object.entries(groupedCriteria).map(([lineName, items]) => (
                      <div key={lineName} className={classes.lineGroup}>
                        <div
                          className={`${classes.lineGroupHeader} ${
                            expandedGroups[lineName]
                              ? classes.lineGroupHeaderOpen
                              : ""
                          }`}
                          onClick={() => toggleGroup(lineName)}
                        >
                          <div className={classes.lineGroupTitle}>
                            {lineName}
                            <span className={classes.lineGroupBadge}>
                              {items.length}{" "}
                              {items.length === 1
                                ? t("ctsguide.criteria.criteria_count_one")
                                : t("ctsguide.criteria.criteria_count_other")}
                            </span>
                          </div>
                          {expandedGroups[lineName] ? (
                            <ExpandLess
                              className={classes.lineGroupExpandIcon}
                            />
                          ) : (
                            <ExpandMore
                              className={classes.lineGroupExpandIcon}
                            />
                          )}
                        </div>

                        <Collapse in={expandedGroups[lineName]}>
                          <div className={classes.lineGroupContent}>
                            {items.map((item, idx) => (
                              <div
                                key={item.id || idx}
                                className={classes.criteriaCard}
                              >
                                <div className={classes.criteriaName}>
                                  <span className={classes.criteriaNameDot} />
                                  {item.criteria_name?.[lang] ||
                                    item.criteria_name?.pt}
                                </div>

                                {(item.when_to_use?.[lang] ||
                                  item.when_to_use?.pt) && (
                                  <div className={classes.criteriaSection}>
                                    <div
                                      className={classes.criteriaSectionLabel}
                                    >
                                      {t("ctsguide.criteria.when_to_use")}
                                    </div>
                                    <div
                                      className={classes.criteriaSectionText}
                                    >
                                      {item.when_to_use?.[lang] ||
                                        item.when_to_use?.pt}
                                    </div>
                                  </div>
                                )}

                                {(item.pros?.[lang] || item.pros?.pt) && (
                                  <div className={classes.prosSection}>
                                    <div
                                      className={classes.criteriaSectionLabel}
                                    >
                                      ✓ {t("ctsguide.criteria.pros")}
                                    </div>
                                    <div
                                      className={classes.criteriaSectionText}
                                    >
                                      {item.pros?.[lang] || item.pros?.pt}
                                    </div>
                                  </div>
                                )}

                                {(item.cons?.[lang] || item.cons?.pt) && (
                                  <div className={classes.consSection}>
                                    <div
                                      className={classes.criteriaSectionLabel}
                                    >
                                      ✗ {t("ctsguide.criteria.cons")}
                                    </div>
                                    <div
                                      className={classes.criteriaSectionText}
                                    >
                                      {item.cons?.[lang] || item.cons?.pt}
                                    </div>
                                  </div>
                                )}

                                {(item.formula_example?.[lang] ||
                                  item.formula_example?.pt) && (
                                  <div className={classes.criteriaSection}>
                                    <div
                                      className={classes.criteriaSectionLabel}
                                    >
                                      {t("ctsguide.criteria.formula_example")}
                                    </div>
                                    <div className={classes.formulaBlock}>
                                      {item.formula_example?.[lang] ||
                                        item.formula_example?.pt}
                                    </div>
                                  </div>
                                )}

                                {item.applicable_industries &&
                                  item.applicable_industries.length > 0 && (
                                    <div className={classes.criteriaSection}>
                                      <div
                                        className={classes.criteriaSectionLabel}
                                      >
                                        {t(
                                          "ctsguide.criteria.applicable_industries",
                                        )}
                                      </div>
                                      <div
                                        className={classes.industriesContainer}
                                      >
                                        {item.applicable_industries.map(
                                          (industry) => (
                                            <Chip
                                              key={industry}
                                              label={getIndustryLabel(industry)}
                                              size="small"
                                              className={classes.industryChip}
                                            />
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            ))}
                          </div>
                        </Collapse>
                      </div>
                    ))}
                </div>
              </div>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>

      {/* Floating Feedback Button */}
      <FeedbackButton pageOrigin="criteriaLibrary" />
    </div>
  );
}
